// ---------------------------------------------------------------------------
// VISINVOKE — Technician Application (canonical, authenticated)
//
// ONE canonical Technician Application experience. Reached from three entry
// points (DCC Technician panel, Utilities gold box, Account -> Technician tab),
// all of which open/resume THIS route (/dashboard/technician/apply) against the
// SAME authoritative record (one TechnicianAccount per user).
//
// Behaviour required by spec:
//   * pre-fill everything VISINVOKE already knows (account name/email/country);
//   * guided, progressive, conditional sections (only relevant questions shown);
//   * draft autosave + resume (debounced -> POST /v1/technician-application/draft);
//   * submit -> POST /apply  (draft -> pending, or create pending directly);
//   * More Information Required -> reopen SAME application -> POST /resubmit;
//   * authoritative status views for pending / more_info / approved / declined /
//     restricted — the frontend NEVER decides approval, it only displays backend
//     state and submits an application.
//
// No localStorage. Cookie-auth via the shared axios API instance.
// ---------------------------------------------------------------------------
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { glassSurface } from './config/glassTheme';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API from './api';

const GOLD = '#D4AF37';

// Country -> registration scheme + presentation. Australia gets ABN/ACN; other
// countries get a generic international registration selector. The list is a
// convenience; "Other" keeps the form usable worldwide (free-text country).
const COUNTRIES = [
  'Australia', 'New Zealand', 'United States', 'United Kingdom', 'Canada',
  'Ireland', 'India', 'Singapore', 'South Africa', 'Germany', 'France',
  'Netherlands', 'Philippines', 'Malaysia', 'United Arab Emirates', 'Other',
];

const BUSINESS_STRUCTURES = [
  { key: 'sole_trader', label: 'Sole trader / independent technician' },
  { key: 'company',     label: 'Company / corporation' },
  { key: 'partnership', label: 'Partnership' },
  { key: 'contractor',  label: 'Contractor' },
  { key: 'other',       label: 'Other' },
];

const RELATIONSHIPS = [
  { key: 'owner_operator', label: 'Owner / operator' },
  { key: 'director',       label: 'Director' },
  { key: 'sole_trader',    label: 'Sole trader (just me)' },
  { key: 'contractor',     label: 'Contractor' },
  { key: 'employee',       label: 'Employee of a technical business' },
  { key: 'other',          label: 'Other' },
];

// Non-AU generic registration types -> stored registration_scheme + which column
// the number lands in. "none" supports a legitimate sole operator with no formal
// company registration (must not auto-reject).
const REG_TYPES = [
  { key: 'company_number',               scheme: 'CRN',  label: 'Company / corporation number' },
  { key: 'business_registration_number', scheme: 'BRN',  label: 'Business registration number' },
  { key: 'vat_number',                   scheme: 'VAT',  label: 'VAT number' },
  { key: 'ein',                          scheme: 'EIN',  label: 'Tax / employer ID (e.g. EIN)' },
  { key: 'tax_id',                       scheme: 'TAX',  label: 'Other tax / business identifier' },
  { key: 'none',                         scheme: 'NONE', label: 'Not applicable — sole operator, no formal registration' },
];

const SERVICES = [
  'PC / computer repair', 'Laptop repair', 'Server support', 'IT support',
  'Managed IT / MSP', 'Networking', 'Cybersecurity', 'Data recovery',
  'Hardware diagnostics', 'System deployment', 'Windows support',
  'Electronics repair', 'Phone / tablet repair', 'CCTV / security systems',
  'Access control', 'AV systems', 'Smart home', 'POS systems', 'Printers',
  '3D printers', 'Industrial / technical equipment',
];

const REASON_USES = [
  'Diagnostics', 'Repair', 'Deployment', 'System configuration', 'Registry work',
  'Customer servicing', 'Troubleshooting', 'Reporting', 'Security work',
];

const SERVICE_MODELS = [
  { key: 'workshop',   label: 'Workshop' },
  { key: 'storefront', label: 'Storefront' },
  { key: 'mobile',     label: 'Mobile' },
  { key: 'onsite',     label: 'On-site' },
  { key: 'remote',     label: 'Remote' },
  { key: 'mixed',      label: 'Mixed' },
];

const CUSTOMER_TYPES = [
  { key: 'consumer', label: 'Consumer' },
  { key: 'business', label: 'Business' },
  { key: 'both',     label: 'Both' },
];

// Sections. `when` hides a section when it is not relevant to earlier answers.
const SECTIONS = [
  { id: 'account',      title: 'Your VISINVOKE Account' },
  { id: 'applicant',    title: 'About You' },
  { id: 'business',     title: 'Your Business' },
  { id: 'registration', title: 'Business Registration' },
  { id: 'services',     title: 'Technical Services' },
  { id: 'verification', title: 'Business / Public Verification' },
  { id: 'experience',   title: 'Technical Experience' },
  { id: 'usage',        title: 'How You Will Use VISINVOKE Technician' },
  { id: 'additional',   title: 'Additional Information' },
  { id: 'review',       title: 'Review & Submit' },
];

// Top-level (structured column) keys — sent as top-level payload fields.
const TOP_LEVEL_KEYS = [
  'display_name', 'legal_name', 'business_name', 'trading_name',
  'business_structure', 'country', 'region_state', 'city_suburb',
  'contact_email', 'contact_phone', 'website',
  'abn', 'acn', 'gst_registered', 'business_registration_number',
  'company_number', 'tax_id', 'vat_number', 'ein', 'registration_scheme',
  'application_message', 'technician_type',
];

// Everything else in the form state goes into application_detail.
function splitPayload(f) {
  const top = {};
  const detail = {};
  Object.keys(f).forEach((k) => {
    const v = f[k];
    if (TOP_LEVEL_KEYS.includes(k)) top[k] = v;
    else detail[k] = v;
  });
  return { ...top, application_detail: detail };
}

function emptyForm() {
  return {
    // identity / business (top-level)
    display_name: '', legal_name: '', business_name: '', trading_name: '',
    business_structure: '', country: '', region_state: '', city_suburb: '',
    contact_email: '', contact_phone: '', website: '',
    abn: '', acn: '', gst_registered: null, business_registration_number: '',
    company_number: '', tax_id: '', vat_number: '', ein: '', registration_scheme: '',
    application_message: '', technician_type: '',
    // detail
    applicant_full_name: '', applicant_position: '', applicant_relationship: '',
    preferred_contact_email: '', employer_name: '',
    year_commenced: '', years_operating: '', staff_count: '', technical_staff_count: '',
    services: [], services_other: '',
    google_business_url: '', linkedin_url: '', social_url: '', marketplace_url: '', other_listing_url: '',
    personal_years_experience: '', specialisation: '',
    customers_per_month: '', customer_type: '', service_model: '', performs_paid_work: null,
    reason_uses: [], reason_text: '', additional_info: '',
    decl_accurate: false, decl_genuine: false, decl_manual: false,
    decl_no_guarantee: false, decl_revocable: false,
    reg_type: '',   // non-AU generic registration selector (UI-only helper)
  };
}

// Merge an existing application (draft / more-info) back into form state so the
// applicant resumes exactly where they left off. Detail keys are spread flat.
function formFromApp(app, user) {
  const f = emptyForm();
  if (user) {
    f.display_name = (`${user.first_name || ''} ${user.last_name || ''}`).trim();
    f.applicant_full_name = f.display_name;
    f.contact_email = user.email || '';
    f.country = user.country || '';
  }
  if (app) {
    TOP_LEVEL_KEYS.forEach((k) => { if (app[k] !== undefined && app[k] !== null) f[k] = app[k]; });
    const d = app.application_detail || {};
    Object.keys(d).forEach((k) => { f[k] = d[k]; });
    if (!f.applicant_full_name) f.applicant_full_name = f.display_name;
    // Reconstruct the non-AU registration selector from stored scheme.
    if (f.country && f.country !== 'Australia') {
      const rt = REG_TYPES.find((r) => r.scheme === f.registration_scheme);
      if (rt) f.reg_type = rt.key;
    }
  }
  return f;
}

export default function TechnicianApplication() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [app, setApp] = useState(null);            // current backend application (or null)
  const [form, setForm] = useState(emptyForm());
  const [step, setStep] = useState(0);
  const [saveState, setSaveState] = useState('');  // '', 'saving', 'saved', 'error'
  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  const [loadErr, setLoadErr] = useState('');

  const saveTimer = useRef(null);
  const dirtyRef = useRef(false);

  // Which lifecycle bucket are we in?
  const status = app ? app.status : null;
  const mode = useMemo(() => {
    if (!app) return 'new';
    if (status === 'draft') return 'draft';
    if (status === 'more_info_required') return 'more_info';
    if (status === 'active') return 'approved';
    if (status === 'rejected') return 'declined';
    if (['suspended', 'paused', 'banned', 'cancelled'].includes(status)) return 'restricted';
    if (['pending', 'under_review', 'approved', 'expired_review'].includes(status)) return 'pending';
    return 'pending';
  }, [app, status]);

  const editable = mode === 'new' || mode === 'draft' || mode === 'more_info';

  const loadMe = useCallback(() => {
    setLoading(true);
    setLoadErr('');
    API.get('/v1/technician-application/me')
      .then((r) => {
        const a = r.data && r.data.application ? r.data.application : null;
        setApp(a);
        setForm(formFromApp(a, user));
      })
      .catch(() => setLoadErr('Unable to load your technician application right now. Please try again shortly.'))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => { loadMe(); }, [loadMe]);

  // ---- Autosave draft (debounced). Only while creating/continuing a draft. ----
  const persistDraft = useCallback(() => {
    if (!(mode === 'new' || mode === 'draft')) return;
    setSaveState('saving');
    API.post('/v1/technician-application/draft', splitPayload(form))
      .then((r) => {
        if (r.data && r.data.application) setApp(r.data.application);
        dirtyRef.current = false;
        setSaveState('saved');
      })
      .catch(() => setSaveState('error'));
  }, [form, mode]);

  useEffect(() => {
    if (!editable) return;
    if (!dirtyRef.current) return;
    if (mode === 'more_info') return; // more-info edits persist only on explicit resubmit
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { persistDraft(); }, 1500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [form, editable, mode, persistDraft]);

  const set = (patch) => {
    dirtyRef.current = true;
    setSaveState('');
    setForm((prev) => ({ ...prev, ...patch }));
  };
  const toggleIn = (key, value) => {
    dirtyRef.current = true;
    setForm((prev) => {
      const arr = Array.isArray(prev[key]) ? prev[key] : [];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });
  };

  const isAU = form.country === 'Australia';

  // Normalise registration selection into stored columns before submit/save.
  const withRegistration = (payload) => {
    const p = { ...payload };
    if (isAU) {
      p.registration_scheme = 'AU_ABN';
    } else if (form.reg_type) {
      const rt = REG_TYPES.find((r) => r.key === form.reg_type);
      if (rt) p.registration_scheme = rt.scheme;
    }
    return p;
  };

  const goStep = (next) => {
    // Persist a draft when moving between sections (belt-and-braces alongside debounce).
    if ((mode === 'new' || mode === 'draft') && dirtyRef.current) persistDraft();
    setStep(Math.max(0, Math.min(SECTIONS.length - 1, next)));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const declarationsOk = form.decl_accurate && form.decl_genuine && form.decl_manual
    && form.decl_no_guarantee && form.decl_revocable;

  const canSubmit = () => {
    if (!form.applicant_full_name || !form.applicant_full_name.trim()) return 'Please enter your full name.';
    if (!form.country) return 'Please select your country.';
    if (!form.business_name && form.applicant_relationship !== 'sole_trader') {
      // sole traders may operate under their own name
      if (!form.display_name) return 'Please enter your business or trading name.';
    }
    if (!Array.isArray(form.services) || form.services.length === 0) {
      if (!form.services_other) return 'Please select at least one technical service.';
    }
    if (!form.reason_text || !form.reason_text.trim()) return 'Please tell us how you will use VISINVOKE Technician.';
    if (!declarationsOk) return 'Please confirm all declarations before submitting.';
    return '';
  };

  const submit = () => {
    const err = canSubmit();
    if (err) { setSubmitErr(err); return; }
    setSubmitErr('');
    setSubmitting(true);
    // reason_text also seeds the top-level application_message for staff scanning.
    const base = splitPayload(withRegistration({ ...form, application_message: form.reason_text }));
    const endpoint = mode === 'more_info'
      ? '/v1/technician-application/resubmit'
      : '/v1/technician-application/apply';
    API.post(endpoint, base)
      .then((r) => {
        if (r.data && r.data.application) setApp(r.data.application);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((e) => setSubmitErr((e.response && e.response.data && e.response.data.error) || 'Unable to submit right now. Please try again.'))
      .finally(() => setSubmitting(false));
  };

  // ---------------------------------------------------------------- render ----
  if (loading) return <Shell><div style={st.note}>Loading your technician application…</div></Shell>;
  if (loadErr) return <Shell><div style={st.err}>{loadErr}</div></Shell>;

  // Non-editable authoritative status screens.
  if (mode === 'pending') return <Shell><StatusCard tone="review" title="Application Under Review"
    body="Your VISINVOKE Technician application has been submitted for manual review. Technician access will appear on your account if your application is approved." app={app} onBack={() => navigate('/manage')} /></Shell>;
  if (mode === 'approved') return <Shell><StatusCard tone="ok" title="Technician Access Approved"
    body="Your VISINVOKE Technician access is active. Open the Desktop Command Centre to get or launch VISINVOKE Technician." app={app} onBack={() => navigate('/manage')} /></Shell>;
  if (mode === 'declined') return <Shell><StatusCard tone="declined" title="Application Not Approved"
    body="Your VISINVOKE Technician application was not approved. If your circumstances have changed you may contact VISINVOKE about reapplying." app={app} onBack={() => navigate('/manage')} /></Shell>;
  if (mode === 'restricted') return <Shell><StatusCard tone="restricted" title="Technician Access Restricted"
    body="Technician access on this account is currently restricted. Please contact VISINVOKE support." app={app} onBack={() => navigate('/manage')} /></Shell>;

  const section = SECTIONS[step];
  const acctName = (`${(user && user.first_name) || ''} ${(user && user.last_name) || ''}`).trim() || form.display_name || '—';
  const acctEmail = (user && user.email) || form.contact_email || '—';

  return (
    <Shell>
      <div style={st.headRow}>
        <div>
          <div style={st.eyebrow}>VISINVOKE TECHNICIAN</div>
          <h1 style={st.h1}>{mode === 'more_info' ? 'Update Your Technician Application' : 'Technician Application'}</h1>
          <p style={st.lead}>Professional tools for verified technicians and technical service businesses. Technician access is manually verified by VISINVOKE.</p>
        </div>
        <div style={st.savepill(saveState)}>
          {saveState === 'saving' ? 'Saving…' : saveState === 'saved' ? 'Draft saved' : saveState === 'error' ? 'Save failed' : (mode === 'draft' ? 'Draft' : '')}
        </div>
      </div>

      {mode === 'more_info' && (
        <div style={st.moreInfoBanner}>
          <strong>More information required.</strong> VISINVOKE has asked for additional details before your application can proceed.
          Please review and update the relevant sections below, then resubmit. Your original application and its history are preserved.
        </div>
      )}

      {/* Section rail */}
      <div style={st.rail}>
        {SECTIONS.map((s, i) => (
          <button key={s.id} onClick={() => goStep(i)}
            style={{ ...st.railItem, ...(i === step ? st.railItemOn : null) }}>
            <span style={st.railNum(i === step)}>{i + 1}</span>{s.title}
          </button>
        ))}
      </div>

      <div style={st.card}>
        <h2 style={st.h2}>{section.title}</h2>

        {section.id === 'account' && (
          <div style={st.grid2}>
            <ReadonlyField label="VISINVOKE Account Name" value={acctName} />
            <ReadonlyField label="VISINVOKE Account Email" value={acctEmail} />
            <div style={st.full}><div style={st.note}>These come from your VISINVOKE account and are included with your application automatically. You do not need to re-enter them.</div></div>
          </div>
        )}

        {section.id === 'applicant' && (
          <div style={st.grid2}>
            <Field label="Your full name" value={form.applicant_full_name} onChange={(v) => set({ applicant_full_name: v })} required />
            <Field label="Your position / role" value={form.applicant_position} onChange={(v) => set({ applicant_position: v })} placeholder="e.g. Owner, Lead Technician" />
            <Select label="Your relationship to the business" value={form.applicant_relationship}
              onChange={(v) => set({ applicant_relationship: v })} options={RELATIONSHIPS} />
            <Field label="Primary contact phone" value={form.contact_phone} onChange={(v) => set({ contact_phone: v })} />
            <Field label="Preferred business contact email (if different)" value={form.preferred_contact_email} onChange={(v) => set({ preferred_contact_email: v })} placeholder={acctEmail} />
            {form.applicant_relationship === 'employee' && (
              <Field label="Employer / business you work for" value={form.employer_name} onChange={(v) => set({ employer_name: v, business_name: form.business_name || v })} />
            )}
          </div>
        )}

        {section.id === 'business' && (
          <div style={st.grid2}>
            <Field label={form.applicant_relationship === 'employee' ? 'Employer business name' : 'Legal business / company name'} value={form.business_name} onChange={(v) => set({ business_name: v })} />
            <Field label="Trading name (if different)" value={form.trading_name} onChange={(v) => set({ trading_name: v })} />
            <Select label="Business structure" value={form.business_structure} onChange={(v) => set({ business_structure: v })} options={BUSINESS_STRUCTURES} />
            <Select label="Country / jurisdiction" value={form.country} onChange={(v) => set({ country: v, registration_scheme: '' , reg_type: ''})} options={COUNTRIES.map((c) => ({ key: c, label: c }))} required />
            <Field label="State / province / region" value={form.region_state} onChange={(v) => set({ region_state: v })} />
            <Field label="City / suburb" value={form.city_suburb} onChange={(v) => set({ city_suburb: v })} />
            <Field label="Year business commenced" value={form.year_commenced} onChange={(v) => set({ year_commenced: v })} placeholder="e.g. 2018" />
            <Field label="Approx. years operating" value={form.years_operating} onChange={(v) => set({ years_operating: v })} />
            <Field label="Number of staff" value={form.staff_count} onChange={(v) => set({ staff_count: v })} placeholder="A one-person business is fine" />
            <Field label="Approx. number of technical staff" value={form.technical_staff_count} onChange={(v) => set({ technical_staff_count: v })} />
          </div>
        )}

        {section.id === 'registration' && (
          <div>
            <div style={st.note}>A legitimate sole technician without formal company registration can still apply — select the option that fits.</div>
            {isAU ? (
              <div style={st.grid2}>
                <Field label="ABN (Australian Business Number)" value={form.abn} onChange={(v) => set({ abn: v })} />
                <Field label="ACN (if a company)" value={form.acn} onChange={(v) => set({ acn: v })} />
                <Check label="Registered for GST" checked={form.gst_registered === true} onChange={(c) => set({ gst_registered: c ? true : null })} />
              </div>
            ) : (
              <div style={st.grid2}>
                <Select label="Registration type" value={form.reg_type} onChange={(v) => set({ reg_type: v })} options={REG_TYPES} />
                {form.reg_type && form.reg_type !== 'none' && (
                  <Field label="Registration number / identifier"
                    value={form[form.reg_type]}
                    onChange={(v) => set({ [form.reg_type]: v })} />
                )}
                <Field label="Registration jurisdiction (country/state)" value={form.region_state} onChange={(v) => set({ region_state: v })} placeholder="Where the business is registered" />
              </div>
            )}
          </div>
        )}

        {section.id === 'services' && (
          <div>
            <div style={st.note}>Select all technical services you provide. Choose “Other” if your work isn’t listed — don’t force an inaccurate category.</div>
            <div style={st.chips}>
              {SERVICES.map((svc) => (
                <button key={svc} onClick={() => toggleIn('services', svc)}
                  style={{ ...st.chip, ...(form.services.includes(svc) ? st.chipOn : null) }}>{svc}</button>
              ))}
            </div>
            <Field label="Other technical services" value={form.services_other} onChange={(v) => set({ services_other: v })} placeholder="Describe any services not listed above" />
            <Field label="Primary specialisation (short)" value={form.technician_type} onChange={(v) => set({ technician_type: v })} placeholder="e.g. MSP, PC repair, field IT" />
          </div>
        )}

        {section.id === 'verification' && (
          <div style={st.grid2}>
            <Field label="Business website" value={form.website} onChange={(v) => set({ website: v })} placeholder="https://" />
            <Field label="Google Business Profile / map listing" value={form.google_business_url} onChange={(v) => set({ google_business_url: v })} placeholder="https://" />
            <Field label="LinkedIn / business page" value={form.linkedin_url} onChange={(v) => set({ linkedin_url: v })} placeholder="https://" />
            <Field label="Professional / social page" value={form.social_url} onChange={(v) => set({ social_url: v })} placeholder="https://" />
            <Field label="Marketplace / service profile" value={form.marketplace_url} onChange={(v) => set({ marketplace_url: v })} placeholder="https://" />
            <Field label="Other public verification URL" value={form.other_listing_url} onChange={(v) => set({ other_listing_url: v })} placeholder="https://" />
            <div style={st.full}><div style={st.note}>Provide at least one public link if you have one. Legitimate technicians may not have every type — that’s fine.</div></div>
          </div>
        )}

        {section.id === 'experience' && (
          <div style={st.grid2}>
            <Field label="Years you have personally worked in this field" value={form.personal_years_experience} onChange={(v) => set({ personal_years_experience: v })} />
            <Field label="Primary technical specialisation" value={form.specialisation} onChange={(v) => set({ specialisation: v })} />
            <Field label="Approx. customers / jobs per month" value={form.customers_per_month} onChange={(v) => set({ customers_per_month: v })} />
            <Select label="Who do you work for?" value={form.customer_type} onChange={(v) => set({ customer_type: v })} options={CUSTOMER_TYPES} />
            <Select label="Service model" value={form.service_model} onChange={(v) => set({ service_model: v })} options={SERVICE_MODELS} />
            <Check label="I perform paid technical work" checked={form.performs_paid_work === true} onChange={(c) => set({ performs_paid_work: c ? true : null })} />
          </div>
        )}

        {section.id === 'usage' && (
          <div>
            <div style={st.note}>How do you intend to use VISINVOKE Technician?</div>
            <div style={st.chips}>
              {REASON_USES.map((u) => (
                <button key={u} onClick={() => toggleIn('reason_uses', u)}
                  style={{ ...st.chip, ...(form.reason_uses.includes(u) ? st.chipOn : null) }}>{u}</button>
              ))}
            </div>
            <TextArea label="Tell us how you will use VISINVOKE Technician" value={form.reason_text} onChange={(v) => set({ reason_text: v })} required />
          </div>
        )}

        {section.id === 'additional' && (
          <div>
            <TextArea label="Additional information that may help us verify your application"
              value={form.additional_info} onChange={(v) => set({ additional_info: v })}
              placeholder="Newly established business, sole technician without a website, employed by another organisation, specialist field, etc." />
          </div>
        )}

        {section.id === 'review' && (
          <div>
            <div style={st.note}>Please confirm the following before submitting.</div>
            <Check label="The information I have supplied is accurate." checked={form.decl_accurate} onChange={(c) => set({ decl_accurate: c })} />
            <Check label="I am genuinely involved in technical / professional service work." checked={form.decl_genuine} onChange={(c) => set({ decl_genuine: c })} />
            <Check label="I understand Technician access is manually reviewed by VISINVOKE." checked={form.decl_manual} onChange={(c) => set({ decl_manual: c })} />
            <Check label="I understand submitting this application does not guarantee approval." checked={form.decl_no_guarantee} onChange={(c) => set({ decl_no_guarantee: c })} />
            <Check label="I understand Technician access may be revoked if eligibility is no longer met or access is abused." checked={form.decl_revocable} onChange={(c) => set({ decl_revocable: c })} />
            {submitErr && <div style={st.err}>{submitErr}</div>}
            <button style={{ ...st.primary, ...(submitting ? st.primaryDisabled : null) }} disabled={submitting} onClick={submit}>
              {submitting ? 'Submitting…' : (mode === 'more_info' ? 'Submit additional information' : 'Submit application')}
            </button>
          </div>
        )}

        {/* Step nav */}
        <div style={st.navRow}>
          <button style={st.ghost} disabled={step === 0} onClick={() => goStep(step - 1)}>Back</button>
          {step < SECTIONS.length - 1
            ? <button style={st.secondary} onClick={() => goStep(step + 1)}>Next</button>
            : <span />}
        </div>
      </div>
    </Shell>
  );
}

// ------------------------------------------------------------------ pieces ----
function Shell({ children }) {
  return (
    <div style={st.page}>
      <div style={st.container}>{children}</div>
    </div>
  );
}

function StatusCard({ tone, title, body, app, onBack }) {
  const tint = { review: '#0ea5e9', ok: '#10b981', declined: '#dc2626', restricted: '#b45309' }[tone] || GOLD;
  return (
    <div style={st.card}>
      <div style={st.eyebrow}>VISINVOKE TECHNICIAN</div>
      <div style={{ ...st.statusBadge, color: tint, borderColor: tint + '55', background: tint + '14' }}>{title}</div>
      <p style={{ ...st.lead, marginTop: 14 }}>{body}</p>
      {app && app.submitted_at && <div style={st.note}>Submitted: {new Date(app.submitted_at).toLocaleDateString()}</div>}
      <button style={{ ...st.secondary, marginTop: 18 }} onClick={onBack}>Back to account</button>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, required }) {
  return (
    <label style={st.fieldWrap}>
      <span style={st.label}>{label}{required ? <span style={{ color: '#dc2626' }}> *</span> : null}</span>
      <input style={st.input} value={value || ''} placeholder={placeholder || ''} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
function ReadonlyField({ label, value }) {
  return (
    <label style={st.fieldWrap}>
      <span style={st.label}>{label}</span>
      <input style={{ ...st.input, background: '#f3f4f6', color: '#4b5563' }} value={value || ''} readOnly />
    </label>
  );
}
function TextArea({ label, value, onChange, placeholder, required }) {
  return (
    <label style={st.fieldWrap}>
      <span style={st.label}>{label}{required ? <span style={{ color: '#dc2626' }}> *</span> : null}</span>
      <textarea style={st.textarea} value={value || ''} placeholder={placeholder || ''} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}
function Select({ label, value, onChange, options, required }) {
  return (
    <label style={st.fieldWrap}>
      <span style={st.label}>{label}{required ? <span style={{ color: '#dc2626' }}> *</span> : null}</span>
      <select style={st.input} value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">Select…</option>
        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
    </label>
  );
}
function Check({ label, checked, onChange }) {
  return (
    <label style={st.checkRow}>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} style={{ marginTop: 3 }} />
      <span>{label}</span>
    </label>
  );
}

const st = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg,#0f172a,#1f2937)', padding: '32px 16px' },
  container: { maxWidth: 900, margin: '0 auto' },
  headRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 18 },
  eyebrow: { color: GOLD, fontSize: 12, fontWeight: 800, letterSpacing: '0.14em' },
  h1: { color: '#fff', fontSize: 26, margin: '6px 0 6px', fontWeight: 800 },
  lead: { color: '#cbd5e1', fontSize: 14, lineHeight: 1.6, maxWidth: 640 },
  savepill: (s) => ({ fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 999, whiteSpace: 'nowrap',
    color: s === 'error' ? '#fecaca' : '#0a0a0a', background: s === 'error' ? '#7f1d1d' : (s ? GOLD : 'transparent'),
    border: s ? 'none' : '1px solid transparent' }),
  moreInfoBanner: { background: '#7c2d12', color: '#fed7aa', border: '1px solid #9a3412', borderRadius: 12, padding: '14px 18px', fontSize: 14, lineHeight: 1.6, marginBottom: 16 },
  rail: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 },
  railItem: { display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 999, padding: '7px 14px', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' },
  railItemOn: { background: GOLD, color: '#0a0a0a', borderColor: GOLD },
  railNum: (on) => ({ display: 'inline-flex', width: 18, height: 18, alignItems: 'center', justifyContent: 'center', borderRadius: 999, fontSize: 11, fontWeight: 800, background: on ? '#0a0a0a' : 'rgba(255,255,255,0.15)', color: on ? GOLD : '#e5e7eb' }),
  card: { background: '#fff', borderRadius: 16, padding: '26px 28px', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' },
  h2: { fontSize: 18, fontWeight: 800, color: '#111827', margin: '0 0 18px' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: 16 },
  full: { gridColumn: '1 / -1' },
  fieldWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 12.5, fontWeight: 700, color: '#374151' },
  input: { border: '1px solid #d1d5db', borderRadius: 9, padding: '10px 12px', fontSize: 14, color: '#111827', outline: 'none', background: '#fff', width: '100%', boxSizing: 'border-box' },
  textarea: { border: '1px solid #d1d5db', borderRadius: 9, padding: '10px 12px', fontSize: 14, color: '#111827', outline: 'none', minHeight: 120, resize: 'vertical', fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 8, margin: '12px 0 18px' },
  chip: { background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 999, padding: '7px 13px', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer' },
  chipOn: { background: GOLD, borderColor: GOLD, color: '#0a0a0a' },
  checkRow: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 14, color: '#374151', lineHeight: 1.5, margin: '10px 0', cursor: 'pointer' },
  note: { fontSize: 13, color: '#6b7280', lineHeight: 1.6, background: '#f9fafb', border: '1px solid #eef0f3', borderRadius: 10, padding: '12px 14px', margin: '0 0 14px' },
  err: { color: '#b91c1c', fontSize: 14, fontWeight: 600, margin: '12px 0' },
  navRow: { display: 'flex', justifyContent: 'space-between', marginTop: 24, paddingTop: 18, borderTop: '1px solid #f0f0f0' },
  primary: { ...glassSurface(GOLD, GOLD, GOLD), borderRadius: 10, padding: '13px 26px', fontSize: 15, fontWeight: 800, cursor: 'pointer', marginTop: 18 },
  primaryDisabled: { background: '#8a7422', color: '#555', cursor: 'not-allowed' },
  secondary: { ...glassSurface('#334155', '#64748B', '#94A3B8'), borderRadius: 10, padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  ghost: { background: 'transparent', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 10, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  statusBadge: { display: 'inline-block', marginTop: 12, fontSize: 16, fontWeight: 800, padding: '8px 16px', borderRadius: 10, border: '1px solid' },
};
